import { FC } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { AiOfficeUser } from '@frontend/phone-ui';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { callIntelAiUtils, callIntelUtils } from '../utils';
import { OfficeUserName } from './user-name';

interface OfficeUserDisplayProps {
  officeUser?: CallIntelTypes.OfficeUser | null;
}

type CallResponderProps = OfficeUserDisplayProps;

interface CallResponderNamespace extends FC<CallResponderProps> {
  AiOfficeUserDisplay: typeof AiOfficeUserDisplay;
  OfficeUserDisplay: typeof OfficeUserDisplay;
}

export const CallResponder: CallResponderNamespace = ({ officeUser }) => {
  return callIntelAiUtils.isAiOfficeUser(officeUser) ? (
    <CallResponder.AiOfficeUserDisplay />
  ) : (
    <CallResponder.OfficeUserDisplay officeUser={officeUser} />
  );
};

const AiOfficeUserDisplay = () => {
  const { t } = useTranslation('call-intel-core');

  return (
    <Text css={styles.container} size='medium'>
      {t('Office User:')}{' '}
      <Text as='span' weight='bold' size='medium'>
        <AiOfficeUser />
      </Text>
    </Text>
  );
};

const OfficeUserDisplay: FC<OfficeUserDisplayProps> = ({ officeUser }) => {
  const { t } = useTranslation('call-intel-core');

  return (
    <Text size='medium' css={styles.container}>
      {t('Office User:')}{' '}
      <Text as='span' weight='bold' size='medium'>
        <OfficeUserName
          showAvatar={false}
          displayName={callIntelUtils.getFullName(officeUser?.firstName, officeUser?.lastName, t('Unknown'))}
        />
      </Text>
    </Text>
  );
};

CallResponder.AiOfficeUserDisplay = AiOfficeUserDisplay;
CallResponder.OfficeUserDisplay = OfficeUserDisplay;

const styles = {
  container: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
  `,
};
